import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { analyticsAPI } from '@/lib/api';
import { OverviewAnalytics, RecentActivity } from '@/types/analytics';
import { Users, Mail, MessageSquare, TrendingUp, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '@/lib/utils';

export function DashboardPage() {
  const navigate = useNavigate();

  const { data: overviewResponse, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['analytics', 'overview'],
    queryFn: () => analyticsAPI.getOverview('30d'),
  });

  const { data: activityResponse, isLoading: activityLoading } = useQuery({
    queryKey: ['analytics', 'recent-activity'],
    queryFn: () => analyticsAPI.getRecentActivity(5),
  });

  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (overviewError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load dashboard data</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  const overview: OverviewAnalytics | undefined = overviewResponse?.data as OverviewAnalytics | undefined;
  const recentActivity: RecentActivity[] = (activityResponse?.data as RecentActivity[]) || [];

  const stats = [
    {
      title: 'Total Leads',
      value: overview ? formatNumber(overview.totalLeads) : '0',
      change: overview ? `${overview.leadsChange >= 0 ? '+' : ''}${overview.leadsChange.toFixed(1)}%` : '0%',
      changeType: overview && overview.leadsChange >= 0 ? 'positive' : 'negative',
      icon: Users,
    },
    {
      title: 'Campaigns',
      value: overview ? formatNumber(overview.totalCampaigns) : '0',
      change: overview ? `${overview.campaignsChange >= 0 ? '+' : ''}${overview.campaignsChange.toFixed(1)}%` : '0%',
      changeType: overview && overview.campaignsChange >= 0 ? 'positive' : 'negative',
      icon: Mail,
    },
    {
      title: 'Responses',
      value: overview ? formatNumber(overview.totalResponses) : '0',
      change: overview ? `${overview.responsesChange >= 0 ? '+' : ''}${overview.responsesChange.toFixed(1)}%` : '0%',
      changeType: overview && overview.responsesChange >= 0 ? 'positive' : 'negative',
      icon: MessageSquare,
    },
    {
      title: 'Conversion Rate',
      value: overview ? `${overview.conversionRate}%` : '0%',
      change: overview ? `${overview.conversionRateChange >= 0 ? '+' : ''}${overview.conversionRateChange.toFixed(1)}%` : '0%',
      changeType: overview && overview.conversionRateChange >= 0 ? 'positive' : 'negative',
      icon: TrendingUp,
    },
  ];

  const quickActions = [
    {
      title: 'Import Leads',
      description: 'Upload a CSV file or add leads manually',
      action: () => navigate('/leads'),
      icon: Users,
    },
    {
      title: 'Create Campaign',
      description: 'Start a new email campaign',
      action: () => navigate('/campaigns'),
      icon: Mail,
    },
    {
      title: 'Review Follow-ups',
      description: 'Check AI suggestions for follow-ups',
      action: () => navigate('/follow-up'),
      icon: MessageSquare,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back! Here's what's happening with your lead generation.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">
                <span className={stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'}>
                  {stat.change}
                </span>{' '}
                from last month
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Get started with common tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {quickActions.map((action) => (
              <div
                key={action.title}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                onClick={action.action}
              >
                <div className="flex items-center space-x-3">
                  <action.icon className="h-6 w-6 text-primary" />
                  <div>
                    <h3 className="font-medium">{action.title}</h3>
                    <p className="text-sm text-muted-foreground">{action.description}</p>
                  </div>
                </div>
                <Plus className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest updates from your campaigns
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activityLoading ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : recentActivity.length > 0 ? (
                recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className={`h-2 w-2 rounded-full ${
                      activity.type === 'campaign_sent' ? 'bg-green-500' :
                      activity.type === 'leads_imported' ? 'bg-blue-500' :
                      activity.type === 'email_opened' ? 'bg-yellow-500' :
                      activity.type === 'email_replied' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`}></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.description}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-muted-foreground">No recent activity</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Chart Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Overview</CardTitle>
          <CardDescription>
            Your lead generation performance over the last 30 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center bg-muted/10 rounded-lg">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Charts coming soon</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}